<template>
  <div class="dashboard">
    <div class="dashboard-light-bg">
      <div class="messages-wrapper">
        <message
          v-if="!userIsWorker && !userIsReceptionist && errors && errors['show-account-confirmation-notice']"
          type="error">
          <strong>{{ $t('dashboard.warning') }}</strong>
          {{ $t('dashboard.confirmEmailAddress') }}
          <a
            href="#"
            @click.prevent="resendConfirmationEmail"
            :style="confirmationIsSending ? 'pointer-events: none;' : null">
            <template v-if="confirmationIsSending">
              {{ $t('dashboard.resendSending') }}
            </template>
            <template v-else>
              {{ $t('dashboard.resendConfirmation') }}
            </template>
          </a>
        </message>

        <message
          v-if="(userIsWorker || userIsReceptionist) && $store.state.config.user.id !== 0 && !$store.state.config.user.confirmed_at"
          type="error">
          <strong>{{ $t('dashboard.warning') }}</strong>
          {{ $t('dashboard.confirmEmailAddressWorker') }}
          <a
            href="#"
            @click.prevent="resendConfirmationEmail"
            :style="confirmationIsSending ? 'pointer-events: none;' : null">
            <template v-if="confirmationIsSending">
              {{ $t('dashboard.resendSending') }}
            </template>
            <template v-else>
              {{ $t('dashboard.resendConfirmation') }}
            </template>
          </a>
        </message>

        <message
          v-if="!systemExpired && reservationsDisabled"
          type="error">
          {{ $t('dashboard.pluginReservationsDisabled') }} <router-link to="/settings/plugin/">{{ $t('dashboard.clickToEnableReservations') }}</router-link>.
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && warnings['system-will-expire-soon'] && expirationDays >= 0"
          :type="expirationDays > 5 ? 'warning' : 'error'">
          <strong>{{ $t('dashboard.warning') }}</strong>
          <template v-if="expirationDays > 0">
            {{ $t('dashboard.yourPackageExpiresIn') }} {{ expirationDays }} {{ expirationDays === 1 ? $t('dashboard.day') : $t('dashboard.days') }}. <router-link to="/billing/">{{ $t('dashboard.clickToRenew') }}</router-link>.
          </template>
          <template v-if="expirationDays === 0">
            {{ $t('billing.expiresSoon') }}. <router-link to="/billing/">{{ $t('dashboard.clickToRenew') }}</router-link>.
          </template>
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && errors['show-account-expired-notice']"
          type="error">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.yourAccountExpired') }} <router-link to="/billing">{{ $t('dashboard.buySubscription') }}</router-link> {{ $t('dashboard.toUseBookero') }}
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && errors['show-sms-counter-notice']"
          type="error">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.smsActiveNoSmsToSend') }} <router-link to="/billing/">{{ $t('dashboard.buySmsPackage') }}</router-link>
        </message>

        <message
          v-if="errors['show-google-calendar-notice']"
          type="warning">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.googleCalendarActiveNoConnectedCalendar') }} <a :href="$config.apiServerUrl + '/panel/google-calendar/redirect-to-google?token=' + token">{{ $t('dashboard.connectGoogleCalendar') }}</a>
        </message>

        <message
          v-if="errors['show-outlook-calendar-notice']"
          type="warning">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.outlookCalendarActiveNoConnectedCalendar') }} <a :href="$config.apiServerUrl + '/panel/outlook-calendar/redirect-to-outlook?token=' + token">{{ $t('dashboard.connectOutlookCalendar') }}</a>
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && errors['show-services-without-workers-notice']"
          type="warning">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.servicesWithoutWorkers') }}
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && errors['show-workers-without-services-notice']"
          type="warning">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.workersWithoutServices') }}
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && errors['show-no-services-notice']"
          type="warning">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.servicesEnabledNoActiveService') }}
        </message>

        <message
          v-if="!userIsWorker && !userIsReceptionist && errors['show-no-payment-methods-notice']"
          type="warning">
          <strong>{{ $t('dashboard.warning') }}</strong> {{ $t('dashboard.paymentsEnabledNoPaymentMethod') }}
        </message>

        <template v-if="Array.isArray(additionalNotices)">
          <message
            v-for="(notice, index) of additionalNotices"
            :key="'custom-notice-' + index"
            :type="notice.type">
            <span v-pure-html="notice.message"></span>
          </message>
        </template>
      </div>

      <div
        v-if="!inquiriesDisabled && userCanAddReservation"
        class="dashboard-waiting">
        <heading :title="$t('dashboard.waitingRequests')">
          <div
            v-if="waitingInquiries.length > 0"
            class="waiting-inquiries-block">
            <span class="waiting-inquiries-counter">
              {{ waitingInquiriesCount }}
            </span>
            <span>
              {{ reservationWordVariations(waitingInquiriesCount) }} {{ $t('dashboard.waitsForAccept') }}
            </span>
          </div>

          <btn
            :type="!waitingInquiriesAsBlocks ? 'icon icon-larger active': 'icon icon-larger'"
            iconName="menu-no-fill"
            iconColor="color-text-light"
            @click.native="displayWaitingInquiriesAsList" />

          <btn
            :type="waitingInquiriesAsBlocks ? 'icon icon-larger active': 'icon icon-larger'"
            iconName="layers-thin"
            iconColor="color-text-light"
            @click.native="displayWaitingInquiriesAsBlocks" />
        </heading>

        <div
          :class="{ 'waiting-inquiries-list': true, 'more-is-hidden': waitingInquiriesHidden }"
          :data-cols="waitingInquiriesCols">
          <template v-for="(groupData, index) in groupedWaitingInquiries">
            <template v-if="groupData.inquiries.length === 1">
              <reservation
                :reservationData="groupData.inquiries[0]"
                :key="'wi-' + index"
                :isBlock="waitingInquiriesAsBlocks"
                :daysMode="groupData.inquiries[0].type === 1" />
            </template>
            <template v-else>
              <reservations-cart
                :reservationsData="groupData.inquiries"
                :cartID="groupData.cart_id"
                :key="'rc-' + index"
                :isBlock="waitingInquiriesAsBlocks" />
            </template>
          </template>
        </div>

        <btn
          v-if="toggleDisplay"
          class="toggle-inquiries"
          @click.native="toggleInquiries">
          <template v-if="waitingInquiriesHidden">{{ $t('dashboard.showAll') }}</template>
          <template v-if="!waitingInquiriesHidden">{{ $t('dashboard.showLess') }}</template>
        </btn>

        <div
          v-if="!waitingInquiries.length && this.$parent.waitingInquiriesLoaded"
          class="waiting-inquiries-empty-state">
          <span>{{ $t('dashboard.noWaitingRequests') }}</span>
        </div>

        <div
          v-if="!waitingInquiries.length && !this.$parent.waitingInquiriesLoaded"
          class="waiting-inquiries-empty-state">
          <span>{{ $t('dashboard.loadingWaitingRequests') }}</span>
        </div>
      </div>

      <div
        v-if="!userIsWorker"
        class="dashboard-stats">
        <heading :title="$t('dashboard.yourPanel')">
          <date-picker
            :language="$t('common.ui.datepickerLanguage')"
            :full-month-name="true"
            :value="statsStartDate"
            @selected="setStartDate"
            name="startDate"
            :format="$t('common.ui.datepickerFormat')"
            :monday-first="true"
            :disabled="statsStartDateDisabled"></date-picker>
          <date-picker
            :language="$t('common.ui.datepickerLanguage')"
            :full-month-name="true"
            :value="statsEndDate"
            @selected="setEndDate"
            name="endDate"
            :format="$t('common.ui.datepickerFormat')"
            :monday-first="true"></date-picker>
        </heading>

        <stats />
      </div>
    </div>

    <tabs
      :items="reservationTabs"
      id="bookero-dashboard-reservation-tabs"
      storageType="localStorage"
      :onToggle="() => false">
      <todays-reservations
        v-if="hasServicesForHours"
        slot="tab-today"
        :key="'todays-reservations'"
        :showOnlyUpcomingReservations="showOnlyUpcomingReservations"
        :services="services"
        :servicesCategories="servicesCategories"
        :servicesLoaded="servicesLoaded"
        :workers="workers"
        :workersLoaded="workersLoaded" />
      <days-reservations
        v-if="hasServicesForDays"
        slot="tab-days"
        :key="'days-reservations'"
        :services="services"
        :servicesCategories="servicesCategories"
        :servicesLoaded="servicesLoaded" />
      <today-timeline
        v-if="workersEnabled && !this.userIsWorker"
        slot="tab-timeline"
        :key="'today-timeline'" />
      <week-reservations
        slot="tab-week"
        :key="'week-reservations'"
        :services="services"
        :servicesCategories="servicesCategories"
        :servicesLoaded="servicesLoaded"
        :workers="workers"
        :workersLoaded="workersLoaded"
        :currentWeekDay="weekCurrentDay" />
    </tabs>

    <reservation-rules-problem-popup />
    <reservations-cart-popup />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import DatePicker from 'vuejs-datepicker';
import ErrorUtils from './../helpers/ErrorUtils.js';
import Heading from './../ui/Heading';
import Tabs from './../ui/Tabs';
import Stats from './../ui/Stats';
import Configuration from './../mixins/Configuration';
import UserTypes from './../mixins/UserTypes';
import WordVariations from './../mixins/WordVariations';
import Reservation from './../ui/Reservation';
import ReservationsCart from './../ui/ReservationsCart';
import ReservationsCartPopup from './../ui/ReservationsCartPopup';
import Responsive from './../mixins/Responsive';
import ReservationRulesProblemPopup from './../ui/ReservationRulesProblemPopup';
import SanitizeUtils from './../helpers/SanitizeUtils';
import TodaysReservations from './../ui/DashboardTodaysReservations';
import DaysReservations from './../ui/DashboardDaysReservations';
import WeekReservations from './../ui/DashboardWeekReservations';
import DashboardTodayTimeline from './../ui/DashboardTodayTimeline';

export default {
  name: 'dashboard',
  mixins: [
    Configuration,
    Responsive,
    UserTypes,
    WordVariations
  ],
  components: {
    'heading': Heading,
    'tabs': Tabs,
    'stats': Stats,
    'date-picker': DatePicker,
    'reservation': Reservation,
    'reservations-cart': ReservationsCart,
    'reservations-cart-popup': ReservationsCartPopup,
    'reservation-rules-problem-popup': ReservationRulesProblemPopup,
    'days-reservations': DaysReservations,
    'todays-reservations': TodaysReservations,
    'week-reservations': WeekReservations,
    'today-timeline': DashboardTodayTimeline
  },
  data () {
    return {
      statsStartDate: new Date(new Date().getTime() - (60 * 60 * 24 * 7 * 1000)),
      statsEndDate: new Date(),
      waitingInquiriesHidden: true,
      waitingInquiriesCols: 5,
      waitingInquiriesAsBlocks: true,
      todaysReservationsLoaded: false,
      daysReservationsLoaded: false,
      confirmationIsSending: false,
      weekReservationsLoaded: false,
      weekCurrentDay: false,
      counters: {
        days: 0,
        today: 0,
        week: 0
      },
      errors: {
        'show-google-calendar-notice': false,
        'show-account-expired-notice': false,
        'show-no-services-notice': false,
        'show-services-without-workers-notice': false,
        'show-workers-without-services-notice': false,
        'show-no-payment-methods-notice': false
      },
      additionalNotices: [],
      expirationDays: 0,
      warnings: {
        'system-will-expire-soon': false
      },
      showOnlyUpcomingReservations: true,
      services: [],
      servicesCategories: [],
      servicesLoaded: false,
      workers: [],
      workersLoaded: false,
      daysReservationsCount: 0,
      todayReservationsCount: 0,
      weekReservationsCount: 0,
      countersInterval: false
    }
  },
  computed: {
    statsStartDateDisabled () {
      return {
        from: new Date(this.statsEndDate.getTime() - (60 * 60 * 24 * 1 * 1000))
      };
    },
    reservationTabs () {
      let tabs = [];

      if (this.hasServicesForHours) {
        tabs.push({
          id: 'today',
          name: this.$t('dashboard.todaysReservations'),
          counter: this.todayReservationsCount,
          isVisible: this.hasServicesForHours
        });
      }

      if (this.hasServicesForDays) {
        tabs.push({
          id: 'days',
          name: this.$t('dashboard.daysReservations'),
          counter: this.daysReservationsCount,
          isVisible: this.hasServicesForDays
        });
      }

      if (this.workersEnabled && !this.userIsWorker) {
        tabs.push({
          id: 'timeline',
          name: this.$t('dashboard.dayPlan'),
          counter: this.todayReservationsCount,
          isVisible: this.workersEnabled && !this.userIsWorker
        });
      }

      if (this.hasServicesForHours) {
        tabs.push({
          id: 'week',
          name: this.$t('dashboard.upcomingWeekReservations'),
          counter: this.weekReservationsCount,
          isVisible: this.hasServicesForHours
        });
      }

      return tabs;
    },
    waitingInquiriesCount () {
      return this.waitingInquiries.length;
    },
    waitingInquiries () {
      return this.$store.state.global.waitingInquiries;
    },
    groupedWaitingInquiries () {
      let groups = [];

      for (let i = 0; i < this.waitingInquiries.length; i++) {
        if (this.waitingInquiries[i].cart_id === 0) {
          groups.push({
            cart_id: this.waitingInquiries[i].cart_id,
            inquiries: [this.waitingInquiries[i]]
          });

          continue;
        }

        let groupIndex = groups.findIndex(group => group.cart_id === this.waitingInquiries[i].cart_id);

        if (groupIndex > -1) {
          groups[groupIndex].inquiries.push(this.waitingInquiries[i]);
        } else {
          groups.push({
            cart_id: this.waitingInquiries[i].cart_id,
            inquiries: [this.waitingInquiries[i]]
          });
        }
      }

      return groups;
    },
    toggleDisplay () {
      if (this.isMobile) {
        return this.groupedWaitingInquiries.length > 2;
      }

      return this.groupedWaitingInquiries.length > this.waitingInquiriesCols;
    },
    inquiriesDisabled () {
      return this.$store.state.config.company.rule_behavior !== '' && !this.waitingInquiriesCount;
    },
    token () {
      return SanitizeUtils.getToken();
    },
    systemExpired () {
      if (this.$store.state.config.expires_days === -1) {
        return true;
      }

      return false;
    },
    reservationsDisabled () {
      return !!this.$store.state.config.company.block_widget;
    },
    hasServicesForHours () {
      return this.servicesEnabled && this.servicesLoaded && this.services.filter(service => service.available_in_dates !== 3).length;
    },
    hasServicesForDays () {
      return this.servicesEnabled && this.servicesLoaded && this.services.filter(service => service.available_in_dates === 3).length;
    }
  },
  mounted: function () {
    this.$moment.locale(this.$config.uiLanguage);
    this.initRWD();

    setTimeout(() => {
      this.$bus.$emit('dashboard-stats-date-range-changed', {
        startDate: this.statsStartDate,
        endDate: this.statsEndDate
      });

      this.loadCounters(true);
      this.loadConfig();
      this.loadNotifications();
    }, 0);

    if (this.$store.state.config.user.id !== 0) {
      this.configurationLoad();
    }

    this.$bus.$on('configuration-loaded', this.configurationLoad);
    this.$bus.$on('show-only-upcoming-reservations-change', this.showOnlyUpcomingReservationsChange);
    this.$bus.$on('expiration-date-update', this.updateExpirationDate);
    this.$bus.$on('dashboard-update-week-reservations-count', this.updateWeekReservationsCount);
    this.$bus.$on('dashboard-update-todays-reservations-count', this.updateTodaysReservationsCount);
    this.$bus.$on('dashboard-update-week-current-day', this.updateWeekCurrentDay);
    this.$bus.$on('dashboard-reservations-list-reload-needed', this.debouncedLoadCounters);
  },
  methods: {
    initRWD () {
      if (window.innerWidth >= 1040 && window.innerWidth < 1240) {
        this.waitingInquiriesCols = 4;
      } else if (window.innerWidth >= 840 && window.innerWidth < 1040) {
        this.waitingInquiriesCols = 3;
        this.waitingInquiriesAsBlocks = true;
      } else if (window.innerWidth >= 540 && window.innerWidth < 840) {
        this.waitingInquiriesCols = 2;
        this.waitingInquiriesAsBlocks = true;
      } else if (window.innerWidth < 540) {
        this.waitingInquiriesCols = 1;
        this.waitingInquiriesAsBlocks = true;
      } else {
        this.waitingInquiriesCols = 5;
        this.waitingInquiriesAsBlocks = true;
      }

      window.addEventListener('resize', this.onWindowResize);
    },
    onWindowResize () {
      let newCount = this.waitingInquiriesCols;

      if (window.innerWidth >= 1040 && window.innerWidth < 1240) {
        newCount = 4;
      } else if (window.innerWidth >= 840 && window.innerWidth < 1040) {
        newCount = 3;
        this.waitingInquiriesAsBlocks = true;
      } else if (window.innerWidth >= 640 && window.innerWidth < 840) {
        newCount = 2;
        this.waitingInquiriesAsBlocks = true;
      } else if (window.innerWidth < 640) {
        newCount = 1;
        this.waitingInquiriesAsBlocks = true;
      } else {
        newCount = 5;
      }

      if (newCount !== this.waitingInquiriesCols) {
        this.waitingInquiriesCols = newCount;
      }
    },
    configurationLoad () {
      setTimeout(() => {
        this.loadServicesAndWorkers();
      }, 1000);

      this.expirationDays = this.$store.state.config.expires_days;

      if (this.expirationDays <= 14 && this.expirationDays >= 0) {
        this.warnings['system-will-expire-soon'] = true;
      }
    },
    updateExpirationDate (expirationDays) {
      this.expirationDays = expirationDays;

      if (this.expirationDays <= 14 && this.expirationDays >= 0) {
        this.warnings['system-will-expire-soon'] = true;
      } else {
        this.warnings['system-will-expire-soon'] = false;
      }
    },
    setStartDate (date) {
      this.statsStartDate = date;

      setTimeout(() => {
        this.$bus.$emit('dashboard-stats-date-range-changed', {
          startDate: this.statsStartDate,
          endDate: this.statsEndDate
        });
      }, 50);
    },
    setEndDate (date) {
      this.statsEndDate = date;

      if (this.statsStartDate.getTime() >= this.statsEndDate.getTime()) {
        this.statsStartDate = new Date(this.statsEndDate.getTime() - (60 * 60 * 24 * 7 * 1000));
      }

      setTimeout(() => {
        this.$bus.$emit('dashboard-stats-date-range-changed', {
          startDate: this.statsStartDate,
          endDate: this.statsEndDate
        });
      }, 50);
    },
    toggleInquiries () {
      this.waitingInquiriesHidden = !this.waitingInquiriesHidden;
    },
    displayWaitingInquiriesAsBlocks () {
      this.waitingInquiriesAsBlocks = true;
      localStorage.setItem('bookero-dashboard-waiting-inquiries-as-block', true);
    },
    displayWaitingInquiriesAsList () {
      this.waitingInquiriesAsBlocks = false;
      localStorage.setItem('bookero-dashboard-waiting-inquiries-as-block', false);
    },
    loadConfig () {
      let inquiriesAsBlock = SanitizeUtils.getLocalStorageValue('bookero-dashboard-waiting-inquiries-as-block', 'boolean', true);

      if (this.waitingInquiriesCols > 4) {
        this.waitingInquiriesAsBlocks = inquiriesAsBlock;
      } else {
        this.waitingInquiriesAsBlocks = true;
      }
    },
    loadNotifications () {
      this.$http.post('/panel/general/get-notices').then(response => {
        this.errors = JSON.parse(JSON.stringify(response.data));
        this.additionalNotices = JSON.parse(JSON.stringify(response.data['custom-notices']));
      }).catch(error => {
        ErrorUtils.handleAjaxError(error, '/panel/general/get-notices', this.$router);
      });
    },
    showOnlyUpcomingReservationsChange (newValue) {
      this.showOnlyUpcomingReservations = newValue;
    },
    loadServicesAndWorkers () {
      this.$http.get('/panel/filters/items').then(response => {
        if (this.workersEnabled && !this.workersLoaded) {
          this.workers = JSON.parse(JSON.stringify(response.data.workers));
          this.workersLoaded = true;
        }

        if (this.servicesEnabled && !this.servicesLoaded) {
          this.services = JSON.parse(JSON.stringify(response.data.services));
          this.servicesCategories = JSON.parse(JSON.stringify(response.data.categories));
          this.servicesCategories.unshift({
            id: 0,
            name: this.$t('common.ui.services.servicesWithoutCategory'),
            ordering: 0
          });
          this.servicesLoaded = true;
        }
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/filters/items', this.$router, this.$bus);
      });
    },
    debouncedLoadCounters: debounce(function () {
      this.loadCounters();
    }, 500),
    loadCounters (initialLoad = false) {
      this.$http.get('/panel/stats/stats-dashboard-inquiries-count').then(response => {
        if (!initialLoad) {
          if (this.todayReservationsCount !== response.data.today_reservations) {
            this.$bus.$emit('dashboard-reservations-list-tab-today-needs-update');
          }

          if (this.daysReservationsCount !== response.data.daily_reservations) {
            this.$bus.$emit('dashboard-reservations-list-tab-days-needs-update');
          }

          if (this.weekReservationsCount !== response.data.week_reservations) {
            this.$bus.$emit('dashboard-reservations-list-tab-week-needs-update');
          }
        }

        this.todayReservationsCount = response.data.today_reservations;
        this.daysReservationsCount = response.data.daily_reservations;
        this.weekReservationsCount = response.data.week_reservations;

        this.countersInterval = setTimeout(() => {
          this.debouncedLoadCounters();
        }, 20000);
      }).catch(error => {
        ErrorUtils.handleLoadError(error, '/panel/stats/stats-dashboard-inquiries-count', this.$router, this.$bus);

        this.countersInterval = setTimeout(() => {
          this.debouncedLoadCounters();
        }, 20000);
      });
    },
    updateWeekReservationsCount (newValue) {
      this.weekReservationsCount = newValue;
    },
    updateTodaysReservationsCount (newValue) {
      this.todayReservationsCount = newValue;
    },
    updateWeekCurrentDay (day) {
      this.weekCurrentDay = day;
      this.$bus.$emit('dashboard-reservations-list-week-day-change');
    },
    resendConfirmationEmail () {
      let dataToSend = null;

      if (this.userIsWorker || this.userIsReceptionist) {
        dataToSend = {
          user_id: parseInt(this.$store.state.config.user.id, 10)
        };
      }

      this.confirmationIsSending = true;

      this.$http.post('/panel/auth/resend-confirmation', dataToSend).then(response => {
        this.confirmationIsSending = false;

        if (response.data.success) {
          this.$bus.$emit('notification-display', {
            message: this.$t('dashboard.confirmationSend'),
            type: 'success'
          });
        }
      }).catch(error => {
        this.confirmationIsSending = false;

        if (error.response && error.response.status === 422) {
          if (error.response.data && error.response.data.errors) {
            let messageKeys = Object.keys(error.response.data.errors);
            let messages = [];

            for (let i = 0; i < messageKeys.length; i++) {
              messages.push(error.response.data.errors[messageKeys[i]]);
            }

            this.$bus.$emit('notification-display', {
              message: messages.join('. '),
              type: 'error'
            });

            if (error.response.data.errors.account_confirmed && this.errors['show-account-confirmation-notice']) {
              delete this.errors['show-account-confirmation-notice'];
            }
          }
        } else {
          ErrorUtils.handleLoadError(error, '/panel/auth/resend-confirmation', this.$router, this.$bus);
        }
      });
    }
  },
  beforeDestroy () {
    clearTimeout(this.countersInterval);
    this.$bus.$off('configuration-loaded', this.configurationLoad);
    this.$bus.$off('show-only-upcoming-reservations-change', this.showOnlyUpcomingReservationsChange);
    this.$bus.$off('expiration-date-update', this.updateExpirationDate);
    this.$bus.$off('dashboard-update-week-reservations-count', this.updateWeekReservationsCount);
    this.$bus.$off('dashboard-update-todays-reservations-count', this.updateTodaysReservationsCount);
    this.$bus.$off('dashboard-update-week-current-day', this.updateWeekCurrentDay);
    this.$bus.$off('dashboard-reservations-list-reload-needed', this.debouncedLoadCounters);
  }
}
</script>

<style lang="scss" scoped>
@import '../../assets/scss/variables.scss';

.dashboard {
  width: 100%;

  .messages-wrapper {
    margin: 0 auto;
    max-width: 1328px;
    overflow: hidden;
  }

  .message {
    margin: 2rem 0 0 0;
    max-width: 100%;
    width: 100%;
  }

  &-light-bg {
    background: $color-bg-lightest;
  }

  &-waiting,
  &-stats {
    margin: 0 auto;
    max-width: 1400px;
    padding: 0 3.6rem;
  }

  &-waiting {
    overflow: hidden;

    & > .button {
      justify-content: center;
      margin: 1rem 0 0 0;
      width: 100%;
    }

    .heading {
      .button {
        margin: 0 0 0 1rem;
      }
    }
  }

  .waiting-inquiries {
    &-block {
      display: flex;
    }

    &-counter {
      background: $color-text-light;
      border-radius: .9rem;
      color: $color-light;
      display: block;
      font-size: 1.3rem;
      font-weight: bold;
      line-height: 1.7rem;
      height: 1.8rem;
      padding: 0 .75rem;
      position: relative;
      z-index: 1;

      &:before {
        border: 2px solid $color-text-light;
        border-radius: .9rem;
        color: $color-text-light;
        content: "?";
        display: inline-block;
        font-size: 1.2rem;
        height: 1.8rem;
        left: -1.6rem;
        line-height: 1.4rem;
        position: absolute;
        text-align: center;
        width: 1.8rem;
        z-index: 0;
      }

      &:after {
        background: transparent;
        border: 2px solid $color-light;
        border-right: none;
        border-bottom: none;
        border-top: none;
        border-radius: 1rem;
        content: "";
        height: 2rem;
        left: -.2rem;
        position: absolute;
        top: -.1rem;
        width: 2rem;
      }

      & + span {
        display: block;
        font-size: 1.2rem;
        line-height: 1.8rem;
        margin-left: .5rem;
        margin-right: 1rem;
      }
    }

    &-empty-state {
      background: $color-bg-light;
      border: none;
      border-radius: $border-radius-small;
      color: $color-text-light;
      margin-bottom: 1rem;
      padding: 2rem;
      text-align: center;
    }
  }

  .waiting-inquiries-list {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    margin: 0 -1.5rem .2rem -1.5rem;
    padding: 2rem 0 0 0;

    & > .reservation,
    & > .reservations-cart {
      margin-left: 1.5rem;
      margin-right: 1.5rem;
      max-width: none;
      width: calc(20% - 3rem);
    }

    &[data-cols="4"] > .reservation,
    &[data-cols="4"] > .reservations-cart {
      width: calc(25% - 3rem);
    }

    &[data-cols="3"] > .reservation,
    &[data-cols="3"] > .reservations-cart {
      width: calc(33.333333% - 3rem);
    }

    &[data-cols="2"] > .reservation,
    &[data-cols="2"] > .reservations-cart {
      width: calc(50% - 3rem);
    }

    &[data-cols="1"] > .reservation,
    &[data-cols="1"] > .reservations-cart {
      width: calc(100% - 3rem);
    }

    &.more-is-hidden {
      &[data-cols="5"] > .reservation:nth-of-type(5) ~ .reservation,
      &[data-cols="5"] > .reservation:nth-of-type(5) ~ .reservations-cart {
        display: none;
      }

      &[data-cols="4"] > .reservation:nth-of-type(4) ~ .reservation,
      &[data-cols="4"] > .reservation:nth-of-type(4) ~ .reservations-cart {
        display: none;
      }

      &[data-cols="3"] > .reservation:nth-of-type(3) ~ .reservation,
      &[data-cols="3"] > .reservation:nth-of-type(3) ~ .reservations-cart {
        display: none;
      }

      &[data-cols="2"] > .reservation:nth-of-type(2) ~ .reservation,
      &[data-cols="2"] > .reservation:nth-of-type(2) ~ .reservations-cart {
        display: none;
      }

      &[data-cols="1"] > .reservation:nth-of-type(2) ~ .reservation,
      &[data-cols="1"] > .reservation:nth-of-type(2) ~ .reservations-cart {
        display: none;
      }
    }
  }

  .tabs {
    padding-bottom: 80px;
    position: relative;

    &:after {
      background: $color-bg-medium;
      content: "";
      height: 6rem;
      position: absolute;
      top: 6rem;
      width: 100%;
    }

    &[data-current-counter="0"]:after,
    &[data-current-tab="timeline"]:after {
      display: none;
    }
  }
}

.toggle-inquiries {
  cursor: pointer;
  display: block;
  margin: 2rem auto;
}

.fade-enter-active, .fade-leave-active {
  transition: all .25s ease-out;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: scale(.5);
}

@media (max-width: 1480px) {
  .dashboard {
    & > div > .messages-wrapper {
      max-width: 1328px;
    }

    .waiting-inquiries-list {
      margin: 0 -0.75rem .2rem -0.75rem;

      & > .reservation,
      & > .reservations-cart {
        margin-left: 0.75rem;
        margin-right: 0.75rem;
      }

      &[data-cols="5"] > .reservation,
      &[data-cols="5"] > .reservations-cart {
        width: calc(20% - 1.5rem);
      }

      &[data-cols="4"] > .reservation,
      &[data-cols="4"] > .reservations-cart {
        width: calc(25% - 1.5rem);
      }

      &[data-cols="3"] > .reservation,
      &[data-cols="3"] > .reservations-cart {
        width: calc(33.333333% - 1.5rem);
      }

      &[data-cols="2"] > .reservation,
      &[data-cols="2"] > .reservations-cart {
        width: calc(50% - 1.5rem);
      }

      &[data-cols="1"] > .reservation,
      &[data-cols="1"] > .reservations-cart {
        width: calc(100% - 1.5rem);
      }
    }
  }
}

@media (max-width: 1440px) {
  .dashboard {
    & > div > .messages-wrapper {
      margin: 0 3.6rem;
      max-width: 1400px;
    }
  }
}

@media (max-width: 1240px) {
  .dashboard {
    &-waiting,
    &-stats {
      padding: 0 1.5rem;
    }

    & > div > .messages-wrapper {
      margin: 0 1.6rem;
    }
  }
}

@media (max-width: 1040px) {
  .dashboard {
    &-stats {
      margin-top: 2rem;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/views/Dashboard.vue